import { MutableRefObject, Dispatch } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import { DemoImageData, addImage, ImagesDataArray } from "../store/features/imagesDataSlice";
import { addExLog } from "../store/features/exLogSlice";
import { v4 as uuidv4 } from 'uuid';
import { decode } from "tiff";
import { updateState } from "../store/features/generalStatesSlice";
import { BufferType } from "tiff/lib/types";
import tifFileMap from "./tifFileMap";
import pdfFileMap from "./pdfFileMap";
import Requester from "../class/Requester";

export function device(): "mobile" | "ipad" | "pc" | "unknown device" {
  if (window.innerWidth <= 767) {
    return "mobile"
  } else if (window.innerWidth > 767 && window.innerWidth <= 1024) {
    return "ipad"
  } else if (window.innerWidth > 1024) {
    return "pc"
  } else {
    return "unknown device";
  }
}

export function deepClone(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  let result: any;
  if (Array.isArray(obj)) {
    result = [];
    for (let i = 0; i < obj.length; i++) {
      result[i] = deepClone(obj[i]);
    }
  } else {
    result = {};
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[key] = deepClone(obj[key]);
      }
    }
  }
  return result;
}

export function hexEncode(str: string): string {
  let hex;
  let result = "";
  for (let i = 0; i < str.length; i++) {
    hex = str.charCodeAt(i).toString(16);
    result += ("0" + hex).slice(-2) + " ";
  }
  result = result.substring(0, result.length - 1);
  return result;
}

export function randomString(length: number) {
  let str = 'abcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = length; i > 0; --i) {
    result += str[Math.floor(Math.random() * str.length)];
  }
  return result;
}

export async function convertUrlToBlob(url: string): Promise<Blob> {
  return await new Promise((rs, rj) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.onload = function () {
      rs(xhr.response);
    }
    xhr.onerror = function () {
      rj(xhr.statusText);
    }
    xhr.send();
  })
}

export async function getCvsFromPdf(url: string): Promise<Array<HTMLCanvasElement>> {
  return await new Promise((rs, rj) => {
    const pdfjsLib = (window as any).pdfjsLib;
    let loadingTask = pdfjsLib.getDocument(url);
    loadingTask.promise.then(async function (pdf: any) {
      const cvsArr: Array<HTMLCanvasElement> = [];
      for (let i = 0; i < pdf.numPages; i++) {
        let pageNumber = i + 1;
        let page = await pdf.getPage(pageNumber);
        let scale = 1;
        let viewport = page.getViewport({ scale });
        let canvas: HTMLCanvasElement = document.createElement('canvas');
        let context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        let renderContext = {
          canvasContext: context,
          viewport: viewport,
          annotationMode: 0
        };
        let renderTask = page.render(renderContext);
        await renderTask.promise;
        cvsArr.push(canvas);
      }
      rs(cvsArr);
    }, function (reason: any) {
      rj(reason);
    });
  });
}

export async function getCvsFromTif(blob: Blob): Promise<Array<HTMLCanvasElement>> {
  const buffer = await new Promise<BufferType>(function (rs, rj) {
    let fr = new FileReader();
    fr.onload = function () {
      rs(fr.result as ArrayBuffer);
    };
    fr.onerror = function () {
      rj(fr.error);
    };
    fr.readAsArrayBuffer(blob);
  });
  let tiff;
  let cvsArr: Array<HTMLCanvasElement> = [];
  try {
    tiff = new (window as any).Tiff({ buffer });
    for (let i = 0, len = tiff.countDirectory(); i < len; ++i) {
      tiff.setDirectory(i);
      cvsArr.push(tiff.toCanvas());
    }
  } catch {
    tiff = decode(buffer);
    const canvas = document.createElement('canvas');
    const context = canvas!.getContext('2d');

    const imageWidth = tiff[0].width;
    const imageHeight = tiff[0].height;
    canvas.width = imageWidth;
    canvas.height = imageHeight;
    const float32ImageData = tiff[0].data;

    const imageData = context!.createImageData(imageWidth, imageHeight);

    for (let i = 0; i < float32ImageData.length; i++) {
      const value = float32ImageData[i] * 255;
      imageData.data[i * 4] = value;
      imageData.data[i * 4 + 1] = value;
      imageData.data[i * 4 + 2] = value;
      imageData.data[i * 4 + 3] = 255;
    }
    context!.putImageData(imageData, 0, 0);
    cvsArr.push(canvas);
  }
  return Promise.resolve(cvsArr);
}

export function sortFiles(fileList: File[]): File[] {
  return Array.from(fileList).sort((a, b) => {
    if (a.type === 'application/pdf' && b.type !== 'application/pdf') {
      return 1;
    } else if (a.type !== 'application/pdf' && b.type === 'application/pdf') {
      return -1;
    } else {
      return 0;
    }
  });
}

export async function handleFiles(fileList: Array<File>, loadingRef: MutableRefObject<HTMLDivElement | null>, dispatch: Dispatch<AnyAction>): Promise<void> {
  const urlArr: ImagesDataArray = [];
  const files: Array<File> = sortFiles(fileList);
  let bHaveUnSupportFile = true;
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (!["image/bmp", "image/gif", "image/jpeg", "image/png", "image/webp", "application/pdf", "image/tiff"].includes(file.type)) continue;
    bHaveUnSupportFile = false;
    await new Promise((rs) => {
      (window as any).EXIF.getData(file, rs);
    });
    const info = (window as any).EXIF.getAllTags(file);
    let url = URL.createObjectURL(file);
    let imgDataObj: Array<DemoImageData> = [];
    if (file.type === "application/pdf") {
      dispatch(updateState({ stateName: "loadingContent", value: "parsing the pdf" }));
      loadingRef.current!.style.display = "block";
      const cvsArr = await getCvsFromPdf(url);
      const fileName = getImgFileName() + ".pdf";
      for (let j = 0; j < cvsArr.length; j++) {
        let blobUrl = cvsArr[j].toDataURL("image/png");
        await new Promise<void>((rs) => {
          cvsArr[j].toBlob((blob: Blob | null) => {
            if (blob) {
              blobUrl = URL.createObjectURL(blob);
            }
            rs();
          })
        })
        imgDataObj.push({ name: fileName, url: blobUrl, sampleImg: false, pageIndex: j, isPdf: true });
      }
      pdfFileMap.set(fileName, file);
      dispatch(updateState({ stateName: "loadingContent", value: "uploading file" }));
      Requester.uploadPdfToServer(fileName, file);
    } else if (await getImageFileType(file) === "tiff") {
      dispatch(updateState({ stateName: "loadingContent", value: "parsing the tiff" }));
      loadingRef.current!.style.display = "block";
      const cvsArr = await getCvsFromTif(file);
      const fileName = getImgFileName() + ".tif";
      for (let j = 0; j < cvsArr.length; j++) {
        let blobUrl = cvsArr[j].toDataURL("image/png");
        await new Promise<void>((rs) => {
          cvsArr[j].toBlob((blob: Blob | null) => {
            if (blob) {
              blobUrl = URL.createObjectURL(blob);
            }
            rs();
          })
        })
        imgDataObj.push({ name: fileName, url: blobUrl, uploadState: false, sampleImg: false, pageIndex: j, isTif: true });
      }
      tifFileMap.set(fileName, file);
    } else {
      let angle: number = 0;
      if (JSON.stringify(info) !== '{}') {
        if (info.Orientation === 3) {
          angle = 180;
        } else if (info.Orientation === 6) {
          angle = -90;
        } else if (info.Orientation === 8) {
          angle = 90;
        }
      }
      const fileName = getImgFileName() + file.name.replace(/.+(?=\.)/, "");
      imgDataObj.push({ name: fileName, url, uploadState: false, sampleImg: false, angle });
    }
    urlArr.push(...imgDataObj);
  }
  if (bHaveUnSupportFile) {
    dispatch(addExLog({ content: `The file format is not supported, please check again.`, isError: true }));
  }
  dispatch(addImage(urlArr));
}

async function getImageFileType(file: File): Promise<"tiff" | "unKnown"> {
  return await new Promise((rs, rj) => {
    const reader = new FileReader();
    reader.onload = function (e: ProgressEvent<FileReader>) {
      const result = e.target!.result;
      const u8 = new Uint8Array(result as ArrayBuffer);
      if ((u8[0] === 0x49 && u8[1] === 0x49) || (u8[0] === 0x4D && u8[1] === 0x4D)) {
        rs("tiff");
      } else {
        rs("unKnown");
      }
    };
    reader.onerror = rj;
    reader.readAsArrayBuffer(file);
  })
}

export function checkIsLink(str: string): boolean {
  return /^(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)|^[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})(\.[a-zA-Z0-9]{2,})?/.test(str);
}

export function getImgFileName(): string {
  return uuidv4();
}

export function animation(element: HTMLElement, speed: number, bFormat?: boolean): void {
  const displayState = getComputedStyle(element).getPropertyValue('display');
  if (displayState === "none") {
    element.style.display = "block";
  }
  let start: DOMHighResTimeStamp | undefined;
  let previousTimeStamp: DOMHighResTimeStamp | undefined;
  let done = false;
  let elapsed: DOMHighResTimeStamp | undefined;
  let h = parseInt(getComputedStyle(element).getPropertyValue('height'));
  let w = parseInt(getComputedStyle(element).getPropertyValue('width'));
  let r = h / w;
  const step = (timestamp: DOMHighResTimeStamp) => {
    if (start === undefined) {
      start = timestamp;
    }
    elapsed = timestamp - start;
    if (displayState === "none") {
      if (previousTimeStamp !== timestamp) {
        const height = Math.min(speed * r * elapsed, h);
        const width = Math.min(speed * elapsed, w);
        element.style.height = `${height}px`;
        element.style.width = `${width + 1}px`;
        if (height === h && width === w) {
          done = true;
          element.style.height = `auto`;
        };
      }
    } else {
      if (previousTimeStamp !== timestamp) {
        let height = Math.max(h - (speed * r * elapsed), 0);
        let width = Math.max(w - (speed * elapsed), 0);
        element.style.height = `${height}px`;
        element.style.width = `${width}px`;
        if (height === 0 && width === 0) {
          done = true;
          element.style.display = "none";
          element.style.height = "auto";
          element.style.width = bFormat ? "85%" : "auto";
        };
      }
    }
    if (elapsed < 1000) {
      previousTimeStamp = timestamp;
      if (!done) requestAnimationFrame(step);
    }
  }
  requestAnimationFrame(step);
}

export function tap2ToTap4(settings: string) {
  const _settings = (JSON as any).stringify(JSON.parse(settings), null, 4, function (_: any, value: any) {
    if (value instanceof Array) {
      return JSON.stringify(value);
    } else {
      return value;
    }
  }, 2);
  return _settings;
}

export const openLiveChat = (isBetchBarcodeScan: boolean) => {
  if (!(window as any).Comm100API || !(window as any).Comm100API.get) return;
  let fields = (window as any).Comm100API.get("livechat.prechat.fields");
  let product_fields = fields?.find((e: any) => e.type === "product");
  if (!product_fields) return;
  if (isBetchBarcodeScan) {
    product_fields.value = "Batch Barcode Scan (20 or more)";
  }
  (window as any).Comm100API.set("livechat.prechat.fields", fields);
  (window as any).Comm100API.do('livechat.button.click');
}